import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * A custom element that fills itself with the products the visitor looked at before.
 *
 * The ids live in localStorage, so the markup can only be built in the browser. We ask
 * the Section Rendering API for this same section at the search route — the same trick
 * the predictive search drawer uses — and lift the finished cards out of the response.
 */
class RecentlyViewedList extends HTMLElement {
  /** @type {boolean} Guards against a second fetch when the editor re-inserts the element. */
  #filled = false;

  connectedCallback() {
    const clearButton = this.querySelector('[data-rv-clear]');

    clearButton?.addEventListener('click', () => {
      RecentlyViewed.clearProducts();
      this.hidden = true;
    });

    if (this.#filled) return;
    this.#filled = true;

    this.#fill();
  }

  async #fill() {
    const ids = this.#ids();

    if (ids.length === 0) {
      // An empty section reads as broken in the editor, so say why it is empty there.
      if (window.Shopify?.designMode) {
        const empty = this.querySelector('[data-rv-empty]');
        if (empty) empty.hidden = false;
        this.hidden = false;
      }
      return;
    }

    const cards = await this.#fetchCards(ids);
    if (!cards) return;

    // The search route answers in relevance order; put the cards back in the order they were viewed.
    const ordered = ids.map((id) => cards.get(id)).filter(Boolean);
    if (ordered.length === 0) return;

    const target = this.querySelector('[data-rv-items]');
    if (!target) return;

    target.replaceChildren(...ordered);
    // Three cards should fill the row, not sit in three quarters of it.
    this.style.setProperty('--recently-viewed-columns', String(Math.min(ordered.length, 4)));
    this.hidden = false;
  }

  /**
   * @returns {string[]} Viewed product ids, minus the product already on screen.
   */
  #ids() {
    const exclude = this.dataset.exclude;
    const limit = Number.parseInt(this.dataset.limit ?? '4', 10);

    return RecentlyViewed.getProducts()
      .map(String)
      .filter((id) => id !== exclude)
      .slice(0, Number.isNaN(limit) ? 4 : limit);
  }

  /**
   * @param {string[]} ids
   * @returns {Promise<Map<string, Element> | null>} Cards keyed by product id.
   */
  async #fetchCards(ids) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return null;

    const url = new URL(window.Theme?.routes?.search_url || '/search', window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url);
      if (!response.ok) return null;

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = new Map();

      for (const card of parsed.querySelectorAll('[data-rv-id]')) {
        cards.set(card.getAttribute('data-rv-id'), card);
      }

      return cards;
    } catch {
      return null;
    }
  }
}

if (!customElements.get('recently-viewed-list')) {
  customElements.define('recently-viewed-list', RecentlyViewedList);
}
